import React, {
  useRef, useCallback, useState, useEffect,
} from 'react';
import { withRouter, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from 'api';
import util from 'utils/utils';
import smoothscroll from 'smoothscroll-polyfill';

import NotFound from 'components/NotFound';
import PageLoadError from 'components/lib/PageLoadError';
import { useIdeaEditor, useEditControls, useAssessmentEditor } from './hooks';
import IdeaStatusBar from './StatusBar';
import EditToolbar from './EditToolbar';
import IdeaHeader from './Header';
import IdeaSideBar from './SideBar';
import IdeaBody from './Body';

import IdeaHelmet from './Components/IdeaHelmet';
import AssignIdeaModal from './Components/AssignIdea';

smoothscroll.polyfill();

export const IdeaPage = ({
  match, user, t, history,
}) => {
  const isIdeaPage = util.activePath(/\/ideas([A-z0-9])*\/?([A-z0-9])*\/?$/gi);
  const ideaId = match?.params?.id;

  const {
    ideaState, updateIdea, undoChanges, redoChanges, canUndo, canRedo, otherUsers, websocketConnected, externalChanges,
  } = useIdeaEditor(ideaId);
  const [emptyRequiredField, setEmptyRequiredField] = useState(false);
  const {
    idea,
    wordCounts,
    isSubmissible,
    incorrectFields,
    isLoading,
    isLoaded,
    isSaving,
    errorMessage,
    notFound,
    savedAt,
  } = ideaState;
  const ideaChallenge = idea?.challenge;
  const userId = user?._id;

  const editControls = useEditControls(user, idea);
  const [editState, setEditing] = editControls;
  const {
    canEdit,
    isEditing,
  } = editState;

  const [assessmentState, updateAssessment] = useAssessmentEditor(idea);
  const [isSubmittingAssessment, setIsSubmittingAssessment] = useState(false);
  const {
    assessment,
    isLoading: assessmentLoading,
    savedAt: assessmentSavedAt,
  } = assessmentState;

  const [assignIdeaModalOpen, setAssignIdeaModalOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [exporting, setExporting] = useState(false);

  const sectionRefs = useRef({});

  const [ideaSetupComplete, setIdeaSetupComplete] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [authType, setAuthType] = useState(null);

  const ideaUser = idea?.user;
  const ideaAuthToken = idea?.authToken;

  /*
    Handle any post-load effects
    For now this is opening the assignment modal, if the idea was previously anonymous, or the url contains an auth token for sms based ideas
  */
  const setupIdea = useCallback(() => {
    if (!isSettingUp && !ideaSetupComplete && isLoaded && userId && !ideaUser) {
      setIsSettingUp(true);
      let publicAuthToken = null;
      if (util.localStorageIsSupported()) {
        publicAuthToken = localStorage.getItem(`ideaAuthToken:${ideaId}`);
      }
      const params = new URLSearchParams(window.location.search);
      const smsAuthToken = params.get('smsAuthToken');
      if (publicAuthToken && (ideaAuthToken === publicAuthToken)) {
        setAssignIdeaModalOpen(true);
        setAuthToken(publicAuthToken);
      } else if (smsAuthToken && (ideaAuthToken === smsAuthToken)) {
        setAssignIdeaModalOpen(true);
        setAuthToken(smsAuthToken);
        setAuthType('sms');
      }
      setIdeaSetupComplete(true);
      setIsSettingUp(false);
    }
  }, [ideaId, isLoaded, ideaUser, ideaAuthToken, ideaSetupComplete, isSettingUp, userId]);

  useEffect(() => setupIdea(), [setupIdea]);

  /*
    Once idea is loaded, scroll to the part of the page requested
  */
  useEffect(() => {
    let hashTimer;
    if (isLoaded) {
      hashTimer = setTimeout(() => {
        const { hash } = document.location;
        if (hash) {
          const elem = document.getElementById(hash.replace('#', ''));
          if (elem) elem.scrollIntoView();
        }
      }, 500);
    }
    return () => clearTimeout(hashTimer);
  }, [isLoaded]);

  const setRef = useCallback((ref, itemId) => {
    sectionRefs.current[itemId] = ref;
  }, []);

  // Attempt to scroll to where the user was previously editing, when changing into preview mode
  // Whenever the user switches to preview, we save their scroll position when editing. Once returned to editing, we scroll back there
  const [scrollPosition, setScrollPosition] = useState(0);
  const [editingScrollPosition, setEditingScrollPosition] = useState(0);
  const handleScroll = useCallback(() => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const timeout = useRef();
  const setEditingAndScroll = useCallback((newIsEditing) => {
    setEditing(newIsEditing);
    if (!newIsEditing) {
      setEditingScrollPosition(scrollPosition);
    } else {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      timeout.current = setTimeout(() => {
        window.scrollTo({ top: editingScrollPosition, left: 0, behavior: 'smooth' });
      }, 500);
    }
  }, [editingScrollPosition, scrollPosition, setEditing]);

  const scrollToRequired = useCallback((sectionId) => {
    const sectionOffset = sectionRefs.current[sectionId]?.offsetTop || 0;
    window.scrollTo({ top: sectionOffset + 64, left: 0, behavior: 'smooth' });
    setEmptyRequiredField(true);
  }, []);

  const onSubmit = useCallback((isSubmitted) => {
    updateIdea(isSubmitted, ['isSubmitted'], true);
    setEmptyRequiredField(false);
  }, [updateIdea]);

  const submitAssessment = useCallback(() => {
    util.confirm('Ready to submit your assessment?', 'Once submitted you will not be able to change your assessment of this idea.')
      .then(() => {
        setIsSubmittingAssessment(true);
        api.ideas.submitAssessment(ideaId, ({ assessment: newAssessment }) => {
          updateAssessment(newAssessment.isSubmitted, ['isSubmitted'], true);
          updateAssessment(newAssessment.submittedAt, ['submittedAt'], true);
          toast.success('Assessment submitted');
          setIsSubmittingAssessment(false);
        }, (err) => {
          toast.error(`Unable to submit your assessment: ${err.message}`);
          setIsSubmittingAssessment(false);
        });
      }).catch(() => { });
  }, [ideaId, updateAssessment]);

  const deleteIdea = useCallback(() => {
    util.confirm(t('ideas.delete.title'), t('ideas.delete.info'))
      .then(() => {
        api.ideas.delete(ideaId, () => {
          history.push(!user ? `/challenges/${ideaChallenge}` : '/ideas');
          toast(t('generic.deleted', { type: 'Idea' }));
          localStorage.removeItem(`ideaAuthToken:${ideaId}`);
        }, (err) => toast.error(err.message));
      }).catch(() => { });
  }, [ideaId, t, history, user, ideaChallenge]);

  const leaveIdea = useCallback(() => {
    util.confirm(t('ideas.teammates.leave.title'), t('ideas.teammates.leave.info'))
      .then(() => {
        api.ideas.removeCollaborator(ideaId, userId, ({ authors, collaborators }) => {
          updateIdea(authors, ['authors'], true);
          updateIdea(collaborators, ['collaborators'], true);
        }, (err) => toast.error(err.message));
      }).catch(() => { });
  }, [ideaId, userId, t, updateIdea]);

  const assignIdea = useCallback(() => {
    setIsAssigning(true);
    const data = { authToken, authType };
    api.ideas.assign(ideaId, data, () => {
      setAssignIdeaModalOpen(false);
      setIsAssigning(false);
      updateIdea(userId, ['user'], true);
      updateIdea([user], ['authors'], true);
    }, () => setIsAssigning(false));
  }, [ideaId, updateIdea, user, userId, authToken, authType]);

  const exportToPdf = useCallback(() => {
    setExporting(true);
    const toastId = toast.loading('Exporting idea..', { duration: 10000 });
    util.downloadIdeaPdf(idea)
      .then((success) => {
        toast.dismiss(toastId);
        toast(success);
        setExporting(false);
      })
      .catch((err) => {
        toast.dismiss(toastId);
        toast.error(err);
        setExporting(false);
      });
  }, [idea]);

  const removeIdeaFromMerge = useCallback((mergeIdeaId, ideaId, onComplete = () => {}, onError = () => {}) => {
    // If you're removing the 2nd to last idea from a merge, then we delete the merge container, as there'd be no point in having it
    const willDeleteMergeContainer = idea?.children?.length <= 2;
    util.confirm(
      'Are you sure you want to remove this idea from the merge?',
      `The idea will not be deleted and will remain in the challenge. You can re-add it to the merge at any time.${willDeleteMergeContainer ? ' The merge container will be deleted as it will only contain one idea.' : ''}`,
    ).then(() => {
      api.ideas.removeIdeaFromMerge(mergeIdeaId, ideaId, () => {
        if (willDeleteMergeContainer) {
          const remainingIdea = idea.ownerChildren.find((child) => child !== ideaId);
          // If we delete the merge container, return to the challenge
          history.push(`/ideas/${remainingIdea._id}`);
        }
        updateIdea(idea.children.filter((child) => child !== ideaId), ['children'], true, () => {}, false);
        updateIdea(idea.ownerChildren.filter((child) => child._id !== ideaId), ['ownerChildren'], true, () => {}, false);
        onComplete(mergeIdeaId, ideaId);
      }, (err) => {
        toast.error(err.message);
        onError(mergeIdeaId, ideaId);
      });
    })
  }, [history, idea?.children, idea?.ownerChildren, updateIdea]);

  if (errorMessage) return <PageLoadError title={t(user ? 'ideas.errorUser' : 'ideas.errorNoUser')} message={errorMessage} />;
  if (notFound) return <NotFound />;

  return (
    <div style={{ position: 'relative' }}>
      {idea ? <IdeaHelmet idea={idea} /> : null}

      <AssignIdeaModal
        isOpen={assignIdeaModalOpen}
        assignIdea={assignIdea}
        onClose={() => setAssignIdeaModalOpen(false)}
        loading={isAssigning}
      />

      <IdeaStatusBar
        user={user}
        idea={idea}
        isSaving={isSaving}
        isEditing={isEditing}
        setEditing={setEditingAndScroll}
        savedAt={savedAt}
        onSubmit={onSubmit}
        otherUsers={otherUsers}
        websocketConnected={websocketConnected}
        scrollToRequired={scrollToRequired}
        assessment={assessment}
        submitAssessment={submitAssessment}
        isSubmittingAssessment={isSubmittingAssessment}
        incorrectFields={incorrectFields}
        isSubmissible={isSubmissible}
        updateIdea={updateIdea}
      />

      <EditToolbar
        idea={idea}
        user={user}
        savedAt={savedAt}
        isEditing={isEditing}
        undoChanges={undoChanges}
        redoChanges={redoChanges}
        canUndo={canUndo}
        canRedo={canRedo}
        deleteIdea={deleteIdea}
        exportToPdf={exportToPdf}
      />

      <IdeaHeader
        idea={idea}
        loading={isLoading}
        isEditing={isEditing}
        deleteIdea={deleteIdea}
        updateIdea={updateIdea}
        exportToPdf={exportToPdf}
        exporting={exporting}
        leaveIdea={leaveIdea}
      />

      <IdeaSideBar
        idea={idea}
        isEditing={isEditing}
        loading={isLoading}
        isIdeaPage={isIdeaPage}
        sectionRefs={sectionRefs}
        updateIdea={updateIdea}
        canEdit={canEdit}
        leaveIdea={leaveIdea}
        main={(
          <div className='idea'>
            <Switch>
              <Route
                exact
                path='/ideas/:id'
                render={() => (
                  <IdeaBody
                    idea={idea}
                    user={user}
                    challenge={idea?.ownerChallenge}
                    loading={isLoading}
                    externalChanges={externalChanges}
                    setRef={setRef}
                    sectionRefs={sectionRefs}
                    updateIdea={updateIdea}
                    scrollToRequired={scrollToRequired}
                    onSubmit={onSubmit}
                    assessment={assessment}
                    assessmentLoading={assessmentLoading}
                    assessmentSavedAt={assessmentSavedAt}
                    updateAssessment={updateAssessment}
                    submitAssessment={submitAssessment}
                    emptyRequiredField={emptyRequiredField}
                    isSubmissible={isSubmissible}
                    incorrectFields={incorrectFields}
                    wordCounts={wordCounts}
                    usingAssessmentTemplate={idea?.children?.length ? idea?.ownerChallenge?.ownerIdeaTemplate : undefined}
                    removeIdeaFromMerge={removeIdeaFromMerge}
                    {...editState}
                  />
                )}
              />
            </Switch>
          </div>
        )}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default withTranslation()(connect(mapStateToProps)(withRouter(IdeaPage)));
